import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import { colors } from '../theme';
import { useIdentity } from '../useIdentity';

/** Same portal pattern as ApplicationsTable's RowActions — the header has
 * no overflow-clipping ancestor today, but a plain absolutely-positioned
 * dropdown would silently break the moment one is added, same real bug
 * class found there. */
function UserMenu() {
  const identityApi = useApi(identityApiRef);
  const { displayName, initials } = useIdentity();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen((o) => !o);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await identityApi.signOut();
    window.location.href = '/';
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label="Account menu"
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: colors.primary,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {initials}
        </div>
        <span style={{ fontSize: 14, fontWeight: 500, color: colors.text }}>{displayName}</span>
      </button>
      {open &&
        menuPos &&
        createPortal(
          <>
            <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 100 }} />
            <div
              role="menu"
              style={{
                position: 'fixed',
                top: menuPos.top,
                right: menuPos.right,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(16, 24, 40, 0.08)',
                zIndex: 101,
                minWidth: 140,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={handleSignOut}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontSize: 13,
                  color: colors.text,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Sign out
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

/**
 * Every Mumzworld page renders inside this — it's what supplies the
 * top-right bar (notification icon, avatar, username) since Backstage's
 * default chrome is sidebar-only with no equivalent top bar concept. The
 * left sidebar itself comes from modules/nav/Sidebar.tsx, which wraps every
 * page automatically via NavContentBlueprint — this component only owns the
 * content area to the right of it.
 */
export function PageShell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 16,
          padding: '16px 32px',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Link
          to="/notifications"
          aria-label="Notifications"
          style={{ position: 'relative', color: colors.textMuted, display: 'flex' }}
        >
          <NotificationsNoneIcon />
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: colors.danger,
            }}
          />
        </Link>
        <UserMenu />
      </header>
      <div style={{ padding: '32px' }}>{children}</div>
    </div>
  );
}
